<template>
  <div
    v-if="showBanner"
    class="flex flex-row items-start gap-2 rounded-xl p-3 lg:rounded-md"
    :class="{
      'bg-rose-100': severity === 'critical',
      'bg-pumpkin-100': severity === 'warning',
      'border border-rose-500': isTabletOrMobile && severity === 'critical',
      'border border-pumpkin-500': isTabletOrMobile && severity === 'warning',
    }"
    @click="toggle"
    role="button"
    :aria-expanded="expanded"
  >
    <div class="flex flex-shrink-0 items-start pt-1">
      <img
        v-if="severity === 'critical'"
        class="h-5 w-5"
        src="@/assets/alert-icon.svg"
        alt="Critical"
      />
      <img
        v-else-if="severity === 'warning'"
        class="h-5 w-5"
        src="@/assets/warn-icon.svg"
        alt="Warning"
      />
    </div>

    <div class="flex flex-1 flex-col text-left">
      <div class="flex items-start justify-between">
        <p class="font-medium text-steel-1100">
          {{ entry.title }}
        </p>

        <img
          v-if="isTabletOrMobile"
          src="@/assets/chevron-right.svg"
          alt="Chevron"
          class="h-5 w-5 transition-transform duration-200"
          :class="{ 'rotate-90': expanded, 'rotate-0': !expanded }"
        />
      </div>

      <transition name="collapse">
        <div v-show="expanded || !isTabletOrMobile">
          <p class="mt-2 text-sm font-normal text-steel-900">
            {{ entry.description }}
          </p>

          <!-- CTAs -->
          <div
            v-if="entry.cta.submit_coi_request || entry.cta.update_eld_account"
            class="mt-3 flex flex-wrap gap-3"
          >
            <button
              v-if="entry.cta.submit_coi_request"
              type="button"
              class="h-10 items-center gap-1 rounded-md bg-steel-20 px-3 text-sm font-medium text-neutral-subtle-bold hover:bg-steel-300 active:bg-steel-400"
              @click.stop="emit('submit-coi-request')"
            >
              Submit COI Request
            </button>

            <button
              v-if="entry.cta.update_eld_account"
              type="button"
              class="h-10 items-center gap-1 rounded-md bg-steel-20 px-3 text-sm font-medium text-neutral-subtle-bold hover:bg-steel-300 active:bg-steel-400"
              @click.stop="emit('update-eld-account')"
            >
              Update ELD Account
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, onMounted } from 'vue';
import { useScreenSize } from '@/composables/useScreenSize';

const emit = defineEmits(['submit-coi-request', 'update-eld-account']);

const props = defineProps({
  status: { type: String, required: true },
});

const { screenSize } = useScreenSize();
const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

const expanded = ref(true);
onMounted(() => {
  if (isTabletOrMobile.value) expanded.value = false;
});
const toggle = () => {
  if (isTabletOrMobile.value) expanded.value = !expanded.value;
};

/**
 * NOTE: status with severity: null do not show banner.
 */
const statusMap = {
  critical: {
    severity: 'critical',
    title: 'None of the VINs in your ELD connection are found on your COI',
    description:
      "Correct any mistyped VINs within your ELD account, confirm you're connected to the correct ELD account, or submit a request for Highway to go get an updated Certificate of Insurance (COI) from your insurance agency.",
    cta: { submit_coi_request: false, update_eld_account: false },
  },
  warning: {
    severity: 'warning',
    title: 'Some of the vehicles on your ELD account and COI do not match.',
    description:
      'If applicable, correct any mistyped VINs within your ELD account, deactivate any old vehicles within your ELD account, or instruct your insurance agency to send an updated Certificate of Insurance (COI) to Highway with all scheduled autos included.',
    cta: { submit_coi_request: false, update_eld_account: false },
  },

  // some_eld_vin_matches_on_coi: {
  //   severity: 'warning',
  //   title: 'Some of the vehicles on your ELD account and COI do not match.',
  //   description:
  //     'If applicable, correct any mistyped VINs within your ELD account, deactivate any old vehicles within your ELD account, or instruct your insurance agency to send an updated Certificate of Insurance (COI) to Highway with all scheduled autos included.',
  //   cta: { submit_coi_request: true, update_eld_account: true },
  // // },

  // https://highwayorg.slack.com/archives/D08TE352NBY/p1753902059444669
  // the meantime for the banners not operating quite right. We need to disable the banner on the equipment page for "some_eld_vin_matches_on_coi"
  // user types. That carrier base data is not quite accurate and we are showing the data as if their vehicles are all verified, but they are not
  some_eld_vin_matches_on_coi: {
    severity: null,
    title: null,
    description: null,
    cta: { submit_coi_request: false, update_eld_account: false },
  },

  no_eld_vin_matches_on_coi: {
    severity: 'critical',
    title: 'None of the VINs in your ELD connection are found on your COI',
    description:
      "Correct any mistyped VINs within your ELD account, confirm you're connected to the correct ELD account, or submit a request for Highway to go get an updated Certificate of Insurance (COI) from your insurance agency.",
    cta: { submit_coi_request: true, update_eld_account: true },
  },

  no_vins_on_coi: {
    severity: 'critical',
    title: 'The schedule of VINs is missing from your COI',
    description:
      'The auto policy in your COI on file with Highway only covers scheduled autos, and your insurance agency failed to include the schedule of autos (VINs) on your COI. Instruct your insurance agency to send an updated Certificate of Insurance (COI) to Highway.',
    cta: { submit_coi_request: true, update_eld_account: false },
  },

  missing_vins_on_coi: {
    severity: 'critical',
    title: 'The schedule of VINs is missing from your COI',
    description:
      'The auto policy in your COI on file with Highway only covers scheduled autos, and your insurance agency failed to include the schedule of autos (VINs) on your COI. Instruct your insurance agency to send an updated Certificate of Insurance (COI) to Highway.',
    cta: { submit_coi_request: true, update_eld_account: false },
  },

  bipd_not_on_file: {
    severity: 'critical',
    title: 'Your Auto policy is not on file',
    description:
      'Submit a request for Highway to go get your Certificate of Insurance (COI) from your insurance agency.',
    cta: { submit_coi_request: true, update_eld_account: false },
  },

  no_coi: {
    severity: 'critical',
    title: 'Your Auto policy is not on file',
    description:
      'Submit a request for Highway to go get your Certificate of Insurance (COI) from your insurance agency.',
    cta: { submit_coi_request: true, update_eld_account: false },
  },

  no_active_vehicles_found_on_eld: {
    severity: 'critical',
    title: 'No recent movement observed',
    description:
      "Your ELD connection is considered inactive because there's been no recent activity among the vehicles found on your ELD account.",
    cta: { submit_coi_request: false, update_eld_account: false },
  },

  vins_on_eld_match_coi: {
    severity: null,
    title: null,
    description: null,
    cta: { submit_coi_request: false, update_eld_account: false },
  },
  vins_on_eld_exactly_match_coi: {
    severity: null,
    title: null,
    description: null,
    cta: { submit_coi_request: false, update_eld_account: false },
  },
  bipd_not_scheduled_autos: {
    severity: null,
    title: null,
    description: null,
    cta: { submit_coi_request: false, update_eld_account: false },
  },
  oauth_reconsent: {
    severity: 'critical',
    title: 'There is an issue with your current ELD connection',
    description:
      'Highway is unable to access all required data within your ELD account. Please click below to open your ELD account in a new browser tab, and click the Remove App button to disconnect Highway. Once you have done this, please refresh this page and click the Sign In button below to reconnect Highway to your ELD account and resolve the issue.',
    cta: { submit_coi_request: false, update_eld_account: false },
  },
};

const entry = computed(() => statusMap[props.status] ?? statusMap.warning);
const severity = computed(() => entry.value.severity);
const showBanner = computed(
  () => severity.value === 'critical' || severity.value === 'warning'
);
</script>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 200px;
  opacity: 1;
}
</style>
